import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BreakEvenCalculator = () => {
  const [fixedCosts, setFixedCosts] = useState('');
  const [variableCosts, setVariableCosts] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [breakEvenUnits, setBreakEvenUnits] = useState(null);
  const [breakEvenRevenue, setBreakEvenRevenue] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleFixedCostsChange = (e) => {
    setFixedCosts(e.target.value);
    setError('');
  };

  const handleVariableCostsChange = (e) => {
    setVariableCosts(e.target.value);
    setError('');
  };

  const handlePricePerUnitChange = (e) => {
    setPricePerUnit(e.target.value);
    setError('');
  };

  const calculateBreakEven = () => {
    if (!fixedCosts || !variableCosts || !pricePerUnit) {
      setError('Please enter all required values.');
      return;
    }

    const fixed = parseFloat(fixedCosts);
    const variable = parseFloat(variableCosts);
    const price = parseFloat(pricePerUnit);

    if (isNaN(fixed) || isNaN(variable) || isNaN(price) || price <= variable) {
      setError('Please enter valid numbers and ensure price per unit is greater than variable costs.');
      return;
    }

    const units = fixed / (price - variable);
    const revenue = units * price;

    setBreakEvenUnits(units.toFixed(2));
    setBreakEvenRevenue(revenue.toFixed(2));

    // Update chart data
    setChartData({
      labels: ['Fixed Costs', 'Variable Costs', 'Price per Unit', 'Break-Even Units', 'Break-Even Revenue'],
      datasets: [
        {
          label: 'Break-Even Calculation',
          data: [fixed, variable, price, units, revenue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(153, 102, 255, 1)', 'rgba(255, 159, 64, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Fixed Costs: ${fixed.toFixed(2)}`,
      `Variable Costs per Unit: ${variable.toFixed(2)}`,
      `Price per Unit: ${price.toFixed(2)}`,
      `Break-Even Units: Fixed Costs / (Price per Unit - Variable Costs per Unit)`,
      `Break-Even Units: ${fixed.toFixed(2)} / (${price.toFixed(2)} - ${variable.toFixed(2)}) = ${units.toFixed(2)} units`,
      `Break-Even Revenue: Break-Even Units * Price per Unit`,
      `Break-Even Revenue: ${units.toFixed(2)} * ${price.toFixed(2)} = ${revenue.toFixed(2)}`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateBreakEven();
  };

  return (
    <>
      <Helmet>
        <title>Break-Even Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate your break-even point with ease. Determine the number of units and revenue needed to break even using this calculator." />
        <meta name="keywords" content="break-even calculator, calculate break-even, business calculator, financial calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Break-Even Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate your break-even point with ease. Determine the number of units and revenue needed to break even.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fixed Costs</label>
                <input
                  type="number"
                  value={fixedCosts}
                  onChange={handleFixedCostsChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter fixed costs"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Variable Costs per Unit</label>
                <input
                  type="number"
                  value={variableCosts}
                  onChange={handleVariableCostsChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter variable costs per unit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price per Unit</label>
                <input
                  type="number"
                  value={pricePerUnit}
                  onChange={handlePricePerUnitChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter price per unit"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate Break-Even
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {breakEvenUnits !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Break-Even Units: {breakEvenUnits}</p>
                    <p className="text-lg font-medium">Break-Even Revenue: ${breakEvenRevenue}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Break-Even Calculation' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the break-even point?</h3>
                <p className="text-gray-600">
                  The break-even point is the level of production at which the total revenues equal total costs. It helps determine the minimum number of units that need to be sold to cover all costs.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How is the break-even point calculated?</h3>
                <p className="text-gray-600">
                  The break-even point in units is calculated by dividing the total fixed costs by the contribution margin per unit (price per unit - variable costs per unit). The break-even revenue is then calculated by multiplying the break-even units by the price per unit.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for business planning?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is perfect for business planning, helping you understand the minimum sales volume needed to cover costs and start making a profit.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Break-Even Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Fixed Costs</h3>
                <p className="text-gray-600">
                  Input the total fixed costs associated with your business or investment.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Variable Costs per Unit</h3>
                <p className="text-gray-600">
                  Input the variable costs per unit associated with your product or service.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Enter Price per Unit</h3>
                <p className="text-gray-600">
                  Input the price per unit at which you sell your product or service.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Calculate Break-Even</h3>
                <p className="text-gray-600">
                  Click the "Calculate Break-Even" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BreakEvenCalculator;